import React, { useEffect, useState } from 'react';
import api from '../../api';
import { Trash2 } from "lucide-react";

import StockTable from './StockTable';
import StockCards from './StockCards';
import CreateStock from './CreateStock';
import EditStock from './EditStock';
import Pagination from './Pagination';
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { toast } from "../shared/ToastProvider";
import EmptyState from "../shared/EmptyState";
import DeleteConfirmation from "../shared/DeleteConfirmation";
import KpiCards from "../shared/KpiCards";
import { Button } from "../ui/button";

const Stock = () => {
    const [items, setItems] = useState([]);
    const [newStock, setNewStock] = useState({ stock_symbol: '', company_name: '' });
    const [editStock, setEditStock] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 100;

    const [selectedItems, setSelectedItems] = useState(new Set());
    const [selectionMode, setSelectionMode] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [createFormOpen, setCreateFormOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await api.get('/stocks');
                setItems(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des données', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleCreate = async () => {
        try {
            const response = await api.post('/stocks', newStock);
            if (response.data.stock) {
                setItems((prev) => [...prev, response.data.stock]);
            } else {
                setItems((prev) => [...prev, response.data]);
            }
            setNewStock({ stock_symbol: '', company_name: '' });
            setCreateFormOpen(false);
            toast.success('Stock ajouté avec succès');
        } catch (error) {
            if (error.response && error.response.status === 400) {
                toast.error("Ce stock existe déjà et est actif.");
            } else {
                toast.error("Erreur lors de l'ajout");
            }
        }
    };

    const handleUpdate = async () => {
        if (!editStock) return;
        try {
            const response = await api.put(`/stocks/${editStock.stock_symbol}`, editStock);
            setItems((prev) =>
                prev.map((item) =>
                    item.stock_symbol === editStock.stock_symbol ? response.data : item
                )
            );
            setEditStock(null);
            toast.success('Stock mis à jour avec succès');
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const toggleSelection = (symbol) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedItems.size === currentItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(currentItems.map(i => i.stock_symbol)));
        }
    };

    const onEdit = (item) => {
        setEditStock(item);
        setCreateFormOpen(false);
    };

    const onDeleteRequest = (symbol) => {
        setDeleteTarget(symbol);
        setIsDeleteDialogOpen(true);
    };

    const onBulkDeleteRequest = () => {
        setDeleteTarget(Array.from(selectedItems));
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        setIsDeleteDialogOpen(false);
        if (Array.isArray(deleteTarget)) {
            let successCount = 0;
            for (const symbol of deleteTarget) {
                try {
                    await api.delete(`/stocks/${symbol}`);
                    setItems(prev => prev.filter(i => i.stock_symbol !== symbol));
                    successCount++;
                } catch (e) { console.error(e); }
            }
            toast.success(`${successCount} stock(s) supprimé(s)`);
            setSelectedItems(new Set());
            setSelectionMode(false);
        } else {
            try {
                await api.delete(`/stocks/${deleteTarget}`);
                setItems(prev => prev.filter(i => i.stock_symbol !== deleteTarget));
                toast.success('Stock supprimé avec succès');
            } catch (e) {
                toast.error('Erreur lors de la suppression');
            }
        }
        setDeleteTarget(null);
    };

    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

    const handleNextPage = () => {
        if (currentPage < Math.ceil(items.length / rowsPerPage)) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Stocks" />

            {!loading && (
                <KpiCards metrics={[{ value: items.length, label: 'Stocks totaux' }]} />
            )}

            <CreateStock
                newStock={newStock}
                setNewStock={setNewStock}
                onCreate={handleCreate}
                isOpen={createFormOpen}
                setIsOpen={setCreateFormOpen}
            />

            {editStock && (
                <EditStock editStock={editStock} setEditStock={setEditStock} handleUpdate={handleUpdate} />
            )}

            {items.length === 0 && !loading ? (
                <EmptyState
                    title="Aucun stock trouvé"
                    subtitle="Ajoutez votre premier stock pour commencer."
                    actionLabel="Ajouter"
                    onAction={() => setCreateFormOpen(true)}
                />
            ) : (
                <>
                    {selectedItems.size > 0 && (
                        <div className="sticky bottom-4 left-0 right-0 z-40 mb-4 p-3 bg-background border rounded-lg shadow-lg flex items-center justify-between">
                            <span className="text-sm font-medium">{selectedItems.size} sélectionné(s)</span>
                            <Button variant="destructive" size="sm" onClick={onBulkDeleteRequest}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer la sélection
                            </Button>
                        </div>
                    )}
                    <div className="hidden md:block">
                        <StockTable
                            items={currentItems}
                            loading={loading}
                            selectedItems={selectedItems}
                            toggleSelection={toggleSelection}
                            toggleSelectAll={toggleSelectAll}
                            onEdit={onEdit}
                            onDeleteRequest={onDeleteRequest}
                        />
                    </div>
                    <div className="md:hidden">
                        <StockCards
                            items={currentItems}
                            loading={loading}
                            selectedItems={selectedItems}
                            selectionMode={selectionMode}
                            toggleSelection={toggleSelection}
                            onEdit={onEdit}
                            onDeleteRequest={onDeleteRequest}
                            onEnterSelectionMode={() => setSelectionMode(true)}
                        />
                    </div>
                    <Pagination
                        items={items}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        handlePageClick={handlePageClick}
                    />
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteDialogOpen}
                onClose={() => { setIsDeleteDialogOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteConfirmed}
                title={Array.isArray(deleteTarget) ? "Confirmer la suppression multiple" : "Confirmer la suppression"}
                description={Array.isArray(deleteTarget)
                    ? `Voulez-vous vraiment supprimer ${deleteTarget.length} stocks ? Cette action est irréversible.`
                    : "Voulez-vous vraiment supprimer ce stock ? Cette action est irréversible."
                }
                confirmText="Supprimer"
            />
        </Container>
    );
};

export default Stock;
